import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { detectFormat } from '../utils/detector';
import { useTranslation } from '../hooks/useTranslation';
import { ArrowRight, Code2 } from 'lucide-react';
import { motion } from 'framer-motion';
import '../components/JsonWorkspace.css'; // Reuse glass styles

const Home = () => {
    const { t } = useTranslation();
    const [input, setInput] = useState('');
    const navigate = useNavigate();

    const handleGo = () => {
        if (!input.trim()) return;
        const format = detectFormat(input);
        let targetRoute = '/json'; // Default fallback

        if (format === 'xml') targetRoute = '/xml';
        if (format === 'html') targetRoute = '/html';

        navigate(targetRoute, { state: { code: input } });
    };

    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            handleGo();
        }
    };

    return (
        <div className="workspace" style={{ alignItems: 'center', height: '100vh', overflowY: 'auto', paddingBottom: '0', maxWidth: 'none', margin: '0', padding: '0' }}>
            {/* Hero Section - Centered Vertically */}
            <div style={{
                minHeight: '70vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
                padding: '40px 20px'
            }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    style={{ width: '100%', maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px', textAlign: 'center' }}
                >
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
                        <Code2 size={48} color="var(--accent-primary)" />
                        <h1 style={{ fontSize: '48px', fontWeight: '800', margin: 0, background: 'var(--accent-gradient)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.02em' }}>
                            FastDev
                        </h1>
                    </div>

                    <p style={{ color: 'var(--text-secondary)', fontSize: '18px', marginBottom: '24px', maxWidth: '600px', margin: '0 auto 24px auto' }}>
                        {t('subtitle')}
                    </p>

                    <div className="panel" style={{ padding: '4px', display: 'flex', flexDirection: 'column', gap: '0' }}>
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={t('placeholder')}
                            style={{
                                width: '100%',
                                height: '240px',
                                background: 'transparent',
                                border: 'none',
                                padding: '24px',
                                color: 'var(--text-primary)',
                                fontFamily: 'var(--font-mono)',
                                fontSize: '15px',
                                resize: 'none',
                                outline: 'none',
                                lineHeight: '1.6'
                            }}
                        />
                        <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)' }}>
                            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                                {input.length > 0 ? `${input.length} chars` : 'Ready to parse'}
                            </span>
                            <button
                                onClick={handleGo}
                                disabled={!input.trim()}
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: input.trim() ? 'var(--accent-primary)' : 'var(--bg-surface-hover)',
                                    color: input.trim() ? '#fff' : 'var(--text-tertiary)',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: input.trim() ? 'pointer' : 'not-allowed',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'all 0.2s',
                                    boxShadow: input.trim() ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                                }}
                            >
                                {t('startParsing')} <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Features Section - Glass Cards */}
            <div style={{
                maxWidth: '1000px',
                width: '100%',
                padding: '0 24px',
                margin: '0 auto 60px auto',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px'
            }}>
                <section className="panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
                    <h3 style={{ color: 'var(--text-primary)', fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                        <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(59, 130, 246, 0.1)', color: 'var(--accent-primary)' }}>
                            <Code2 size={20} />
                        </div>
                        {t('jsonTools')}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '14px', flex: 1 }}>
                        {t('jsonDesc')}
                    </p>
                </section>

                <section className="panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
                    <h3 style={{ color: 'var(--text-primary)', fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                        <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(139, 92, 246, 0.1)', color: 'var(--accent-secondary)' }}>
                            <ArrowRight size={20} />
                        </div>
                        {t('formatConversion')}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '14px', flex: 1 }}>
                        {t('conversionDesc')}
                    </p>
                </section>

                <section className="panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px', height: '100%' }}>
                    <h3 style={{ color: 'var(--text-primary)', fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600' }}>
                        <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}>
                            <Code2 size={20} />
                        </div>
                        {t('secureLocal')}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6', fontSize: '14px', flex: 1 }}>
                        {t('secureDesc')}
                    </p>
                </section>
            </div>

            <footer style={{
                position: 'relative',
                marginTop: 'auto',
                padding: '20px',
                textAlign: 'center',
                width: '100%',
                color: 'var(--text-tertiary)',
                fontSize: '12px',
                fontFamily: 'var(--font-sans)',
                opacity: 0.8,
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                background: 'transparent'
            }}>
                <a
                    href="https://beian.miit.gov.cn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseOver={(e) => e.target.style.color = 'var(--text-primary)'}
                    onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                    粤ICP备2025403162号
                </a>
                <a
                    href="mailto:lsw@vip.qq.com"
                    style={{ color: 'inherit', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseOver={(e) => e.target.style.color = 'var(--text-primary)'}
                    onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                    {t('contact')}
                </a>
            </footer>
        </div >
    );
};

export default Home;
